import type {
  ReportSankeyNode,
  SankeyScope,
} from '@/modules/analysis/stores/types'
import type { FlowValueFormat } from './flowFormat'
import {
  accountKey,
  displayLabel,
  type FlowOfFunds,
  type FlowRole,
  nodesByName,
  roleOf,
} from './flowModel'
import { flowSummarySentences } from './flowSentences'

export type FlowView = 'all' | SankeyScope

export const FLOW_VIEWS: FlowView[] = ['all', 'external', 'internal']

const OUTSIDE_ROLES: FlowRole[] = ['payer', 'payee']

const TRANSFERS_NOTE =
  "Money in and out of an account can differ because of moves between the client's own accounts."

interface ViewShape {
  keeps: (source: ReportSankeyNode, target: ReportSankeyNode) => boolean
  sourceRole: FlowRole
  targetRole: FlowRole
}

const isOutside = (node: ReportSankeyNode) =>
  OUTSIDE_ROLES.includes(roleOf(node))

// Money in & out: Payers -> Account -> Payees, each account drawn once,
// and no money kept or moved between own accounts.
// Between own accounts: From account -> To account.
const VIEW_SHAPES: Record<SankeyScope, ViewShape> = {
  external: {
    keeps: (source, target) => isOutside(source) || isOutside(target),
    sourceRole: 'account',
    targetRole: 'account',
  },
  internal: {
    keeps: (source, target) =>
      !isOutside(source) &&
      !isOutside(target) &&
      accountKey(source) !== accountKey(target),
    sourceRole: 'from',
    targetRole: 'to',
  },
}

export function flowForView(flow: FlowOfFunds, view: FlowView): FlowOfFunds {
  if (view === 'all') return flow
  const shape = VIEW_SHAPES[view]
  const byName = nodesByName(flow)
  const nodes = new Map<string, ReportSankeyNode>()
  const links = flow.links.flatMap((link) => {
    const source = byName.get(link.source)
    const target = byName.get(link.target)
    if (!source || !target || !shape.keeps(source, target)) return []
    const shownSource = asRole(source, shape.sourceRole)
    const shownTarget = asRole(target, shape.targetRole)
    nodes.set(shownSource.name, shownSource)
    nodes.set(shownTarget.name, shownTarget)
    return [{ ...link, source: shownSource.name, target: shownTarget.name }]
  })
  return { nodes: [...nodes.values()], links }
}

export function flowSummaryForView(
  flow: FlowOfFunds,
  view: FlowView,
  valueFormat: FlowValueFormat,
): string {
  const sentences = flowSummarySentences(flowForView(flow, view), valueFormat)
  const hasTransfers = flowForView(flow, 'internal').links.length > 0
  if (view === 'external' && hasTransfers) sentences.push(TRANSFERS_NOTE)
  return sentences.join(' ')
}

function asRole(node: ReportSankeyNode, role: FlowRole): ReportSankeyNode {
  if (isOutside(node)) return node
  return {
    ...node,
    name: `${accountKey(node)} (${role})`,
    label: displayLabel(node),
    role,
  }
}
